import { useEffect, useRef } from "react";
import type { Call } from "@entities/call";
import type { Uuid } from "@shared/api";

/** Как часто сверяться со сроком: перенос на четверть минуты позже — не беда, а таймер дешевле. */
const TICK_MS = 15_000;

/**
 * Неактивный канал — как AFK в Discord: кто в звонке сервера молчит дольше
 * срока, того переносит в неактивный канал сервера. Молчанием считаем и
 * бездействие в окне: слушающий, который пишет в чате или листает ленту, не
 * спит, и переносить его было бы грубо. Переходит сам звонок — тем же входом,
 * что при переносе модератором, поэтому в звонке видно, куда и почему.
 *
 * Речь сбрасывает отсчёт в тот же миг, а не на тике: иначе короткие фразы
 * между проверками не считались бы. Отказ сервера (нет права войти, канал
 * полон) не повторяется каждые пятнадцать секунд — до смены звонка или канала.
 */
export function useAfk(
  call: Call,
  meId: Uuid,
  /**
   * Неактивный канал сервера, где идёт звонок, и срок в секундах; `null` — не
   * переносить (не задан или это сцена, где слушатель молчит по замыслу).
   */
  afkOf: (channelId: Uuid) => { channelId: Uuid; timeout: number } | null,
): void {
  const live = call.state === "live" ? call.channelId : null;
  const afk = live ? afkOf(live) : null;
  const speaking = call.peers.some((peer) => peer.id === meId && peer.speaking);
  const last = useRef(0);
  const tried = useRef<string | null>(null);
  const joinRef = useRef(call.join);
  joinRef.current = call.join;

  useEffect(() => {
    if (speaking) last.current = Date.now();
  }, [speaking]);

  const target = afk?.channelId ?? null;
  const timeout = afk?.timeout ?? 0;

  useEffect(() => {
    if (!live || !target || target === live) return;

    last.current = Date.now();
    const touch = () => {
      last.current = Date.now();
    };
    const events = ["keydown", "pointerdown", "pointermove", "wheel"] as const;
    for (const name of events) window.addEventListener(name, touch, { passive: true });
    const timer = setInterval(() => {
      const move = `${live}>${target}`;
      if (tried.current === move || Date.now() - last.current < timeout * 1000) return;
      tried.current = move;
      void joinRef.current(target, true);
    }, TICK_MS);

    return () => {
      for (const name of events) window.removeEventListener(name, touch);
      clearInterval(timer);
    };
  }, [live, target, timeout]);
}
